import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import {
  FLAG_LABELS,
  areAdjacent,
  confidenceLevel,
  draftFlags,
  insertLine,
  makeDraftLine,
  mergeLines,
  removeLine,
  setLineSpeaker,
  updateLine,
} from '../../analysis/draft'
import type { AnalysisDraft, DraftLine, DraftRole, LineFlag } from '../../analysis/draft'
import { formatTime, parseTime } from '../../lib/format'
import { MergeLinesDialog } from '../MergeLinesDialog'
import type { MergeCandidate } from '../MergeLinesDialog'

/** Сколько строк рисуем сразу: у полного фильма их тысячи. */
const PAGE = 200

/** Длина строки, которую заводит кнопка «добавить». */
const NEW_LINE_SECONDS = 2

export type AnalysisLinesProps = {
  draft: AnalysisDraft
  disabled: boolean
  /** Текущая позиция видео — от неё заводится новая строка. */
  currentTime: number
  videoDuration: number
  onChange: (next: AnalysisDraft) => void
  onSeek: (time: number) => void
}

type RowProps = {
  line: DraftLine
  index: number
  roles: DraftRole[]
  flags: LineFlag[]
  disabled: boolean
  /** Отмечена для объединения с соседями. */
  merging: boolean
  /** Строку только что добавили руками — прокручиваем к ней. */
  justAdded: boolean
  onToggleMerge: (key: string) => void
  onPatch: (key: string, patch: Partial<Omit<DraftLine, 'key' | 'speakerRaw'>>) => void
  onSpeaker: (key: string, speaker: string) => void
  onDelete: (key: string) => void
  onSeek: (time: number) => void
}

function confidenceText(value: number | null): string {
  if (value === null) return '—'
  return `${Math.round(value * 100)}%`
}

const LineRow = memo(function LineRow({
  line,
  index,
  roles,
  flags,
  disabled,
  merging,
  justAdded,
  onToggleMerge,
  onPatch,
  onSpeaker,
  onDelete,
  onSeek,
}: RowProps): JSX.Element {
  const [start, setStart] = useState(line.start.toFixed(2))
  const [end, setEnd] = useState(line.end.toFixed(2))
  const [original, setOriginal] = useState(line.textOriginal)
  const [text, setText] = useState(line.text)
  const rowRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (justAdded) rowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [justAdded])

  useEffect(() => setStart(line.start.toFixed(2)), [line.start])
  useEffect(() => setEnd(line.end.toFixed(2)), [line.end])
  useEffect(() => setOriginal(line.textOriginal), [line.textOriginal])
  useEffect(() => setText(line.text), [line.text])

  const commitStart = (raw: string): void => {
    const parsed = parseTime(raw)
    if (parsed === null || parsed < 0 || parsed >= line.end) {
      setStart(line.start.toFixed(2))
      return
    }
    if (Math.abs(parsed - line.start) < 0.001) return
    onPatch(line.key, { start: parsed })
  }

  const commitEnd = (raw: string): void => {
    const parsed = parseTime(raw)
    if (parsed === null || parsed <= line.start) {
      setEnd(line.end.toFixed(2))
      return
    }
    if (Math.abs(parsed - line.end) < 0.001) return
    onPatch(line.key, { end: parsed })
  }

  const level = confidenceLevel(line.confidence)
  const suspicious = flags.length > 0
  const nonverbal = line.kind === 'nonverbal'
  const role = roles.find((item) => item.speaker === line.speaker)

  return (
    <div
      ref={rowRef}
      className={`analysis-row${suspicious ? ' suspicious' : ''}${merging ? ' merging' : ''}`}
    >
      <div className="analysis-cell times">
        <div className="row" style={{ gap: 4 }}>
          <input
            type="checkbox"
            checked={merging}
            disabled={disabled}
            aria-label="Отметить для объединения"
            title="Отметить для объединения с соседней строкой"
            onChange={() => onToggleMerge(line.key)}
          />
          <span className="faint mono analysis-index">{index + 1}</span>
          <button
            type="button"
            className="ghost small"
            title="Перемотать видео к началу реплики"
            onClick={() => onSeek(line.start)}
          >
            ▶
          </button>
        </div>
        <input
          className="mono"
          type="text"
          value={start}
          disabled={disabled}
          aria-label="Начало реплики"
          onChange={(e) => setStart(e.currentTarget.value)}
          onBlur={(e) => commitStart(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
        />
        <input
          className="mono"
          type="text"
          value={end}
          disabled={disabled}
          aria-label="Конец реплики"
          onChange={(e) => setEnd(e.currentTarget.value)}
          onBlur={(e) => commitEnd(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
        />
        <span className={`analysis-confidence level-${level}`} title="Уверенность расшифровки">
          {confidenceText(line.confidence)}
        </span>
      </div>

      <div className="analysis-cell">
        <select
          value={line.speaker}
          disabled={disabled}
          aria-label="Говорящий"
          onChange={(e) => onSpeaker(line.key, e.currentTarget.value)}
        >
          <option value="">— без роли —</option>
          {roles.map((item) => (
            <option key={item.speaker} value={item.speaker}>
              {item.name}
            </option>
          ))}
          {line.speaker && !role ? (
            <option value={line.speaker}>{line.speaker} (нет роли)</option>
          ) : null}
        </select>
        {nonverbal ? (
          <span className="badge nonverbal-badge" title="Вокализация без слов: крик, стон, дыхание">
            ◍ без слов
          </span>
        ) : null}
        {flags.length > 0 ? (
          <div className="analysis-flags">
            {flags.map((flag) => (
              <span key={flag} className="badge warn-badge">
                {FLAG_LABELS[flag]}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="analysis-cell">
        <textarea
          value={original}
          rows={2}
          disabled={disabled}
          placeholder={nonverbal ? 'Слов нет — вокализация' : 'Оригинал'}
          aria-label="Оригинальный текст"
          onChange={(e) => setOriginal(e.currentTarget.value)}
          onBlur={() => {
            if (original !== line.textOriginal) onPatch(line.key, { textOriginal: original })
          }}
        />
      </div>

      <div className="analysis-cell">
        <textarea
          value={text}
          rows={2}
          disabled={disabled}
          placeholder={
            nonverbal ? 'Пометка актёру: что за звук' : 'Перевод — то, что произнесёт актёр'
          }
          aria-label="Текст реплики"
          onChange={(e) => setText(e.currentTarget.value)}
          onBlur={() => {
            if (text !== line.text) onPatch(line.key, { text })
          }}
        />
      </div>

      <div className="analysis-cell">
        <button
          type="button"
          className="ghost small"
          disabled={disabled}
          title="Удалить реплику из черновика"
          aria-label={`Удалить реплику ${formatTime(line.start)}`}
          onClick={() => onDelete(line.key)}
        >
          ✕
        </button>
      </div>
    </div>
  )
})

export function AnalysisLines({
  draft,
  disabled,
  currentTime,
  videoDuration,
  onChange,
  onSeek,
}: AnalysisLinesProps): JSX.Element {
  const [onlySuspicious, setOnlySuspicious] = useState(false)
  const [visible, setVisible] = useState(PAGE)
  const [mergeKeys, setMergeKeys] = useState<ReadonlySet<string>>(() => new Set<string>())
  const [mergeOpen, setMergeOpen] = useState(false)
  const [addedKey, setAddedKey] = useState<string | null>(null)

  const flags = useMemo(() => draftFlags(draft), [draft])
  const suspiciousCount = useMemo(
    () => draft.lines.filter((line) => (flags.get(line.key)?.length ?? 0) > 0).length,
    [draft.lines, flags],
  )

  const shown = useMemo(
    () =>
      onlySuspicious
        ? draft.lines.filter((line) => (flags.get(line.key)?.length ?? 0) > 0)
        : draft.lines,
    [draft.lines, flags, onlySuspicious],
  )

  // Номер строки в полном списке — искать его перебором на каждую строку дорого.
  const indexByKey = useMemo(() => {
    const map = new Map<string, number>()
    draft.lines.forEach((line, index) => map.set(line.key, index))
    return map
  }, [draft.lines])

  const patch = (key: string, values: Partial<Omit<DraftLine, 'key' | 'speakerRaw'>>): void => {
    onChange(updateLine(draft, key, values))
  }

  // Стабильная ссылка: строки мемоизированы, и новый обработчик на каждый
  // рендер перерисовывал бы всю таблицу.
  const toggleMerge = useCallback((key: string): void => {
    setMergeKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // Отмеченная строка могла исчезнуть — удалением или прошлым объединением.
  const selected = useMemo(
    () => draft.lines.filter((line) => mergeKeys.has(line.key)),
    [draft.lines, mergeKeys],
  )
  const adjacent = useMemo(() => areAdjacent(draft, mergeKeys), [draft, mergeKeys])

  const mergeCandidates = useMemo<MergeCandidate[]>(
    () =>
      selected.map((line) => {
        const role = draft.roles.find((item) => item.speaker === line.speaker)
        return {
          id: line.key,
          start: line.start,
          end: line.end,
          roleId: line.speaker,
          roleName: role?.name ?? (line.speaker || 'без роли'),
          roleColor: role?.color ?? '',
          text: line.text || line.textOriginal,
          // Черновик ещё не применён — дублям взяться неоткуда.
          takes: 0,
        }
      }),
    [selected, draft.roles],
  )

  const confirmMerge = (speaker: string): void => {
    onChange(mergeLines(draft, mergeKeys, speaker))
    setMergeKeys(new Set<string>())
    setMergeOpen(false)
  }

  const limit = videoDuration > 0 ? videoDuration : draft.duration
  const canAdd = limit <= 0 || currentTime < limit - 0.05

  /**
   * Строка, которую конвейер пропустил (docs/SEGMENTATION.md, «Объединение
   * и создание реплик»): границы — от текущей позиции видео. Новая строка
   * встаёт по таймкоду в середину таблицы, поэтому её приходится показать
   * и прокрутить к ней — иначе кажется, что кнопка ничего не сделала.
   */
  const addLine = (): void => {
    const start = Math.max(0, Number(currentTime.toFixed(2)))
    const raw = limit > 0 ? Math.min(start + NEW_LINE_SECONDS, limit) : start + NEW_LINE_SECONDS
    const end = Number(raw.toFixed(2))
    if (!(end > start)) return
    const speaker = draft.roles[0]?.speaker ?? ''
    const line = makeDraftLine(start, end, speaker)
    const next = insertLine(draft, line)
    const index = next.lines.findIndex((item) => item.key === line.key)
    if (index >= 0) setVisible((current) => Math.max(current, index + 1))
    setAddedKey(line.key)
    onChange(next)
  }

  return (
    <div className="stack">
      <div className="row wrap">
        <h3 style={{ margin: 0 }}>Найденные реплики</h3>
        <span className="faint">{draft.lines.length}</span>
        <span className="spacer" />
        <button
          type="button"
          className="small"
          disabled={disabled || !canAdd}
          title="Добавить строку с текущей позиции видео"
          onClick={addLine}
        >
          + Строка с {formatTime(currentTime)}
        </button>
        <label className="row check" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={onlySuspicious}
            disabled={suspiciousCount === 0}
            onChange={(e) => setOnlySuspicious(e.currentTarget.checked)}
          />
          <span>Только сомнительные ({suspiciousCount})</span>
        </label>
      </div>

      {mergeKeys.size > 0 ? (
        <div className="merge-bar">
          <strong>Выбрано для объединения: {selected.length}</strong>
          <span className="faint">
            {selected.length < 2
              ? 'Отметьте ещё одну соседнюю строку.'
              : adjacent
                ? 'Получится одна строка: начало первой, конец последней, тексты через пробел.'
                : 'Отмеченные строки идут не подряд — объединять можно только соседние.'}
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="primary small"
            disabled={disabled || !adjacent}
            onClick={() => setMergeOpen(true)}
          >
            Объединить ({selected.length})
          </button>
          <button
            type="button"
            className="ghost small"
            onClick={() => setMergeKeys(new Set<string>())}
          >
            Сбросить
          </button>
        </div>
      ) : null}

      <p className="faint" style={{ margin: 0 }}>
        Всё в таблице правится прямо здесь: границы, говорящий, оригинал и перевод. Подсвечены
        строки, где машина скорее всего ошиблась, — но тихие ошибки бывают и в остальных.
        Строки «◍ без слов» — крик, стон или дыхание: слов там нет по существу, и это не ошибка.
        Отметьте галочками две соседние строки, чтобы слить их в одну.
      </p>

      {draft.lines.length === 0 ? (
        <p className="notice warn" style={{ margin: 0 }}>
          Реплик не осталось. Применять нечего — отбросьте черновик или запустите разбор заново.
        </p>
      ) : (
        <>
          <div className="analysis-table">
            <div className="analysis-row head">
              <span className="faint">Таймкод</span>
              <span className="faint">Говорящий</span>
              <span className="faint">Оригинал</span>
              <span className="faint">Перевод</span>
              <span />
            </div>
            {shown.slice(0, visible).map((line) => (
              <LineRow
                key={line.key}
                line={line}
                index={indexByKey.get(line.key) ?? 0}
                roles={draft.roles}
                flags={flags.get(line.key) ?? []}
                disabled={disabled}
                merging={mergeKeys.has(line.key)}
                justAdded={line.key === addedKey}
                onToggleMerge={toggleMerge}
                onPatch={patch}
                onSpeaker={(key, speaker) => onChange(setLineSpeaker(draft, key, speaker))}
                onDelete={(key) => onChange(removeLine(draft, key))}
                onSeek={onSeek}
              />
            ))}
          </div>

          {shown.length > visible ? (
            <div className="row">
              <button type="button" onClick={() => setVisible((v) => v + PAGE)}>
                Показать ещё {Math.min(PAGE, shown.length - visible)} из {shown.length - visible}
              </button>
            </div>
          ) : null}

          {onlySuspicious && shown.length === 0 ? (
            <p className="faint" style={{ margin: 0 }}>
              Сомнительных строк нет. Это не значит, что ошибок нет, — просто их не видно
              по формальным признакам.
            </p>
          ) : null}
        </>
      )}

      {mergeOpen && mergeCandidates.length > 1 ? (
        <MergeLinesDialog
          candidates={mergeCandidates}
          roleOptions={draft.roles.map((role) => ({
            id: role.speaker,
            name: role.name,
            color: role.color,
          }))}
          // Черновик живёт в браузере: применять его ещё только предстоит,
          // терять нечего.
          takesAtRisk={false}
          busy={disabled}
          onConfirm={confirmMerge}
          onCancel={() => setMergeOpen(false)}
        />
      ) : null}
    </div>
  )
}
